import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Shield, Globe, TrendingUp, FileText, Zap, CheckCircle, ChevronRight, Newspaper, Landmark, BarChart3, Ship, AlertTriangle, DollarSign, LineChart, Package, Building2, ShieldCheck, Anchor, Users, Brain, ShieldAlert, Menu, X } from 'lucide-react';
import { toast } from 'sonner';
import { PricingSection } from '@/pages/PricingPage';
import { SEOHead, trackEvent } from '@/components/SEOHead';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [ctaSubmitted, setCtaSubmitted] = useState(false);
  const [ctaError, setCtaError] = useState('');
  const [ctaLoading, setCtaLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleStartTrial = () => {
    navigate('/register');
  };

  const handleScrollToCTA = () => {
    const ctaSection = document.querySelector('#cta-section');
    if (ctaSection) {
      ctaSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCtaSubmit = async () => {
    setCtaError('');
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setCtaError('Please enter a valid email address.');
      return;
    }
    setCtaLoading(true);
    try {
      await axios.post(`${API}/contact/landing`, { email: email.trim(), website: honeypot });
      setCtaSubmitted(true);
    } catch (err) {
      setCtaError('Something went wrong. Please email info@nuvantasolutions.com.au directly.');
    }
    setCtaLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <SEOHead noApi title="Nuvanta TradePath | Supply Chain Risk Intelligence for Australian Business" description="Monitor supply chain risk across 42 countries with AI-powered trade intelligence. Built for Australian importers. Start your free trial today." />
      <header>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A2533]/95 backdrop-blur-sm border-b border-white/10" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-white font-bold text-lg sm:text-xl">Nuvanta Solutions</span>
            <span className="text-[#4DD0E1] text-lg sm:text-xl">|</span>
            <span className="text-[#4DD0E1] font-semibold text-lg sm:text-xl">TradePath</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-white/80 hover:text-white transition-colors text-sm">Features</a>
            <a href="#pricing" className="text-white/80 hover:text-white transition-colors text-sm">Pricing</a>
            <a href="#audiences" className="text-white/80 hover:text-white transition-colors text-sm">Who it's for</a>
            <a href="#about" className="text-white/80 hover:text-white transition-colors text-sm">About</a>
            <a href="/insights" className="text-white/80 hover:text-white transition-colors text-sm">Insights</a>
            <a href="/trust" className="text-white/80 hover:text-white transition-colors text-sm">Trust</a>
            <Button 
              onClick={() => navigate('/signin')}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 rounded-lg px-5"
              data-testid="nav-signin-btn"
            >
              Sign in
            </Button>
            <Button 
              onClick={handleStartTrial}
              className="bg-[#00707C] hover:bg-[#005F6A] text-white rounded-lg px-5"
              data-testid="nav-start-trial-btn"
            >
              Start your trial
            </Button>
          </div>
          <button
            className="md:hidden flex items-center justify-center w-11 h-11 rounded-lg text-white hover:bg-white/10 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>
      {/* Mobile menu overlay - outside nav for proper fixed positioning */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed top-[73px] left-0 right-0 bottom-0 bg-[#0A2533] overflow-y-auto" style={{ zIndex: 999 }} data-testid="mobile-menu-overlay">
          <nav className="flex flex-col p-6 gap-2" aria-label="Mobile navigation">
            {[
              { label: 'Features', href: '#features' },
              { label: 'Pricing', href: '#pricing' },
              { label: "Who it's for", href: '#audiences' },
              { label: 'About', href: '#about' },
              { label: 'Insights', href: '/insights' },
              { label: 'Trust', href: '/trust' },
            ].map(link => (
              <a key={link.label} href={link.href} className="text-white text-lg py-3 px-4 rounded-lg hover:bg-white/10 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                {link.label}
              </a>
            ))}
            <hr className="border-white/10 my-3" />
            <Button onClick={() => { setMobileMenuOpen(false); navigate('/signin'); }} variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-lg w-full justify-center py-3" data-testid="mobile-signin-btn">
              Sign in
            </Button>
            <Button onClick={() => { setMobileMenuOpen(false); handleStartTrial(); }} className="bg-[#00707C] hover:bg-[#005F6A] text-white rounded-lg w-full justify-center py-3 mt-2" data-testid="mobile-start-trial-btn">
              Start your trial
            </Button>
          </nav>
        </div>
      )}
      </header>

      {/* Hero Section */}
      <main id="main-content">
      <section className="relative min-h-screen bg-[#0A2533] pt-20 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/d294f673-4840-4101-882c-d0bbfe348af5/images/1c5ba6ad8616bbefb23a641536b156c51efcf207e8c58b06285ba0ada8780eeb.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A2533]/50 via-[#0A2533]/80 to-[#0A2533]" />
        
        <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-20">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6" data-testid="hero-headline">
              Know what's happening in your supply chain before it hits the board
            </h1>
            <p className="text-lg sm:text-xl text-[#4DD0E1] mb-10 max-w-3xl leading-relaxed" data-testid="hero-subheading">
              TradePath gives Australian procurement teams, government buyers, and defence supply chain professionals a single view of supplier risk — so you can act, not react.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={handleStartTrial}
                className="bg-[#00707C] hover:bg-[#00707C]/90 text-white rounded-lg px-8 py-6 text-lg font-semibold"
                data-testid="hero-start-trial-btn"
              >
                Start your trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline"
                className="border-white text-white hover:bg-white/10 rounded-lg px-8 py-6 text-lg font-semibold bg-transparent"
                data-testid="hero-contact-btn"
                onClick={handleScrollToCTA}
              >
                Talk to our team
              </Button>
            </div>
          </motion.div>

          {/* Floating stats */}
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {[
              { value: '42', label: 'Countries monitored globally' },
              { value: '12', label: 'Live data sources' },
              { value: '24/7', label: 'Signal monitoring' },
              { value: '3', label: 'Audience-tailored views' }
            ].map((stat, i) => (
              <div key={stat.label} className="text-center" data-testid={`hero-stat-${stat.label.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="text-3xl sm:text-4xl font-bold text-[#4DD0E1] mb-1">{stat.value}</div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="py-24 bg-[#F5F5F5]" id="features">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0D3B4F] tracking-tight mb-4">
              Supply chain risk is no longer a once-a-year exercise
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                stat: '73%',
                text: 'of businesses experienced supply chain disruption in the past 12 months',
                icon: TrendingUp
              },
              {
                stat: '$50K+',
                text: 'Existing platforms cost this much per year and are built for US and European markets',
                icon: Globe
              },
              {
                stat: 'Flying blind',
                text: 'Mid-market companies lack visibility on APAC-specific risk signals that affect their suppliers',
                icon: Shield
              }
            ].map((item, i) => (
              <motion.div
                key={item.stat}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white rounded-lg p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] transition-shadow"
              >
                <item.icon className="h-10 w-10 text-[#0097A7] mb-4" />
                <div className="text-3xl font-bold text-[#0D3B4F] mb-3">{item.stat}</div>
                <p className="text-[#4E6974] leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0D3B4F] tracking-tight mb-4">
              From raw signals to clear actions
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-8">
            {[
              {
                step: '01',
                title: 'Data ingestion',
                description: 'We pull from geopolitical event feeds, trade databases, government sources, and sector news across 42 countries globally.',
                icon: Globe
              },
              {
                step: '02',
                title: 'AI synthesis',
                description: 'Our AI engine processes thousands of signals daily, identifies patterns, and assigns structured risk scores.',
                icon: Zap
              },
              {
                step: '03',
                title: 'Risk intelligence',
                description: 'You get plain-English risk briefings that explain what is happening, why it matters, and what to consider.',
                icon: FileText
              },
              {
                step: '04',
                title: 'Action',
                description: 'Set custom alerts, download reports, and brief your team with confidence.',
                icon: CheckCircle
              },
              {
                step: '05',
                title: 'Diversify',
                description: 'Use Sourcing Alternatives to evaluate new source countries by risk, cost, trade agreements, and logistics \u2014 with evidence you can take to the board.',
                icon: Globe
              }
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="relative"
              >
                {i < 4 && (
                  <div className="hidden md:block absolute top-12 left-full w-full h-[2px] bg-gradient-to-r from-[#0097A7] to-[#4DD0E1]" style={{ width: 'calc(100% - 2rem)' }} />
                )}
                <div className="text-xs font-bold text-[#0097A7] tracking-[0.2em] uppercase mb-4">Step {item.step}</div>
                <div className="bg-[#F5F5F5] rounded-full w-16 h-16 flex items-center justify-center mb-4">
                  <item.icon className="h-7 w-7 text-[#0D3B4F]" />
                </div>
                <h3 className="text-xl font-bold text-[#0D3B4F] mb-3">{item.title}</h3>
                <p className="text-[#4E6974] leading-relaxed text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Sources Section */}
      <section className="py-24 bg-[#F5F5F5]" id="data-sources">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0D3B4F] tracking-tight mb-4">
              Powered by twelve global and Australian data sources
            </h2>
            <p className="text-[#4E6974] leading-relaxed max-w-3xl mx-auto text-base md:text-lg">
              TradePath aggregates and analyses real-time data from trusted international and Australian government sources so you don't have to. From geopolitical signals to port congestion, sanctions screening to labour market trends — all updated automatically.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Globe, name: 'GDELT Project', badge: 'Geopolitical signals', description: 'Real-time geopolitical event monitoring and media sentiment analysis across 100+ languages' },
              { icon: Ship, name: 'UN Comtrade', badge: 'Trade flows', description: 'Official United Nations trade flow data covering imports, exports, and commodity volumes' },
              { icon: Landmark, name: 'World Bank', badge: 'Economic indicators', description: 'Macroeconomic indicators including GDP growth, inflation, trade openness, and regulatory quality' },
              { icon: LineChart, name: 'IMF DataMapper', badge: 'Economic forecasts', description: 'International Monetary Fund forecasts and country-level economic outlook data' },
              { icon: Newspaper, name: 'GNews', badge: 'News intelligence', description: 'Breaking news aggregation with supply chain and trade keyword monitoring across global markets' },
              { icon: AlertTriangle, name: 'GDACS', badge: 'Disaster alerts', description: 'United Nations real-time alerts covering earthquakes, cyclones, floods, and volcanic activity' },
              { icon: DollarSign, name: 'Open Exchange Rates', badge: 'Currency risk', description: 'Daily exchange rates across all major trading currencies to track import cost exposure' },
              { icon: BarChart3, name: 'Australian Bureau of Statistics', badge: 'Australian trade', description: 'Official Australian merchandise import and export data by country and commodity' },
              { icon: ShieldAlert, name: 'DFAT Sanctions List', badge: 'Compliance screening', description: 'Australian Government consolidated sanctions list for automated supplier compliance screening' },
              { icon: Brain, name: 'Observatory of Economic Complexity', badge: 'Trade structure', description: 'Economic complexity and product-level trade data for alternative sourcing analysis' },
              { icon: Users, name: 'International Labour Organisation', badge: 'Labour market', description: 'Wages, unemployment, industrial action, and workplace safety data across monitored countries' },
              { icon: Anchor, name: 'MarineTraffic', badge: 'Port congestion', description: 'Port congestion monitoring and vessel wait times across key global shipping hubs' },
            ].map((source, i) => (
              <motion.div
                key={source.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-[#F5F5F5] border border-[#E0E0E0] rounded-lg p-6 hover:shadow-[0_8px_28px_rgba(0,0,0,0.08)] hover:-translate-y-[2px] transition-all duration-200"
                data-testid={`data-source-card-${source.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <source.icon className="h-8 w-8 text-[#0097A7] mb-4" />
                <h3 className="text-base font-bold text-[#0D3B4F] mb-1">{source.name}</h3>
                <span className="inline-block text-[10px] font-semibold tracking-wide uppercase text-white bg-[#00707C] rounded-full px-2.5 py-0.5 mb-3">{source.badge}</span>
                <p className="text-[#4E6974] text-sm leading-relaxed mb-4">{source.description}</p>
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2E7D32] opacity-60" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#2E7D32]" />
                  </span>
                  <span className="text-[#2E7D32] text-xs font-semibold tracking-wide">Live</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience Panels Section — replacing old sector cards */}
      <section className="py-24 bg-white" id="audiences">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0D3B4F] tracking-tight mb-4">
              Built for businesses managing global supply chain risk
            </h2>
            <p className="text-[#4E6974] leading-relaxed max-w-2xl mx-auto text-base md:text-lg">
              TradePath adapts to your role. Select your audience during onboarding and your entire dashboard, reports, and alerts adjust to match.
            </p>
          </motion.div>

          <div className="space-y-8">
            {/* Panel 1: Importers */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#F5F5F5] rounded-xl overflow-hidden"
              data-testid="audience-panel-importer"
            >
              <div className="grid md:grid-cols-5">
                <div className="md:col-span-3 p-8 lg:p-12">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#0097A7]/10 flex items-center justify-center">
                      <Package className="h-5 w-5 text-[#0097A7]" />
                    </div>
                    <span className="text-xs font-bold text-[#0097A7] tracking-[0.15em] uppercase">Mid-market importers</span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-[#0D3B4F] mb-4">
                    Stop flying blind on your suppliers
                  </h3>
                  <p className="text-[#4E6974] leading-relaxed mb-6">
                    You import from China, Vietnam, Thailand, or Indonesia. When a tariff changes, a port backs up, or a currency swings — you find out too late. TradePath gives you the early warning system your business needs.
                  </p>
                  <Button 
                    onClick={handleStartTrial}
                    className="bg-[#00707C] hover:bg-[#00707C]/90 text-white rounded-lg"
                    data-testid="audience-cta-importer"
                  >
                    See how it works for importers
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                <div className="md:col-span-2 bg-[#0D3B4F] p-8 lg:p-10 flex flex-col justify-center">
                  <h4 className="text-[#4DD0E1] text-xs font-bold tracking-[0.15em] uppercase mb-5">Key features</h4>
                  <ul className="space-y-3">
                    {['Supplier risk monitoring', 'Freight rate tracking', 'Currency impact on import costs', 'Tariff and trade policy alerts'].map((f) => (
                      <li key={f} className="flex items-center gap-3 text-white/90 text-sm">
                        <CheckCircle className="h-4 w-4 text-[#4DD0E1] flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Panel 2: Government procurement */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-[#F5F5F5] rounded-xl overflow-hidden"
              data-testid="audience-panel-government"
            >
              <div className="grid md:grid-cols-5">
                <div className="md:col-span-3 p-8 lg:p-12">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#0097A7]/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-[#0097A7]" />
                    </div>
                    <span className="text-xs font-bold text-[#0097A7] tracking-[0.15em] uppercase">Government procurement</span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-[#0D3B4F] mb-4">
                    Sovereign risk assessment for every goods panel
                  </h3>
                  <p className="text-[#4E6974] leading-relaxed mb-6">
                    Government procurement requires demonstrable due diligence on supplier country-of-origin risk. TradePath maps your panel suppliers against real-time geopolitical, economic, and compliance risk data — giving you an auditable risk assessment at any point in time.
                  </p>
                  <Button 
                    onClick={handleStartTrial}
                    className="bg-[#00707C] hover:bg-[#00707C]/90 text-white rounded-lg"
                    data-testid="audience-cta-government"
                  >
                    See how it works for government
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                <div className="md:col-span-2 bg-[#0D3B4F] p-8 lg:p-10 flex flex-col justify-center">
                  <h4 className="text-[#4DD0E1] text-xs font-bold tracking-[0.15em] uppercase mb-5">Key features</h4>
                  <ul className="space-y-3">
                    {['Country-of-origin risk scoring', 'Supplier upload and risk mapping', 'Compliance-ready reporting', 'Sovereign risk dashboards'].map((f) => (
                      <li key={f} className="flex items-center gap-3 text-white/90 text-sm">
                        <CheckCircle className="h-4 w-4 text-[#4DD0E1] flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Panel 3: Defence */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-[#F5F5F5] rounded-xl overflow-hidden"
              data-testid="audience-panel-defence"
            >
              <div className="grid md:grid-cols-5">
                <div className="md:col-span-3 p-8 lg:p-12">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#0097A7]/10 flex items-center justify-center">
                      <ShieldCheck className="h-5 w-5 text-[#0097A7]" />
                    </div>
                    <span className="text-xs font-bold text-[#0097A7] tracking-[0.15em] uppercase">Defence supply chains</span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-[#0D3B4F] mb-4">
                    AUKUS-ready supply chain transparency
                  </h3>
                  <p className="text-[#4E6974] leading-relaxed mb-6">
                    Defence primes and subcontractors face increasing scrutiny on supply chain sovereignty and compliance. TradePath identifies exposure to countries of concern across your supplier base and provides continuous monitoring against AUKUS and defence procurement obligations.
                  </p>
                  <Button 
                    onClick={handleStartTrial}
                    className="bg-[#00707C] hover:bg-[#00707C]/90 text-white rounded-lg"
                    data-testid="audience-cta-defence"
                  >
                    See how it works for defence
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                <div className="md:col-span-2 bg-[#0D3B4F] p-8 lg:p-10 flex flex-col justify-center">
                  <h4 className="text-[#4DD0E1] text-xs font-bold tracking-[0.15em] uppercase mb-5">Key features</h4>
                  <ul className="space-y-3">
                    {['Country-of-concern flagging', 'Supply chain sovereignty scoring', 'Compliance reporting', 'Continuous supplier monitoring', 'AUKUS obligation tracking'].map((f, i) => (
                      <li key={i} className="flex items-center gap-3 text-white/90 text-sm">
                        <CheckCircle className="h-4 w-4 text-[#4DD0E1] flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-24 bg-[#F5F5F5]" id="about">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0D3B4F] tracking-tight mb-4">
              Trusted by supply chain leaders
            </h2>
            <p className="text-[#4E6974] leading-relaxed max-w-2xl mx-auto text-base md:text-lg">
              Built by Nuvanta Solutions — an Australian strategic advisory firm with deep expertise in procurement, government, and digital transformation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              {
                quote: "TradePath gives us visibility we never had before. When the Indonesia nickel export ban hit, we already had alternative sourcing options ready because the platform flagged it weeks in advance.",
                author: "Supply Chain Director",
                company: "ASX-listed mining services company",
                audience: "Importer",
              },
              {
                quote: "The compliance reporting alone saves our procurement team two days per quarter. Being able to demonstrate auditable sovereign risk assessment on every goods panel supplier is a game-changer.",
                author: "Chief Procurement Officer",
                company: "State government procurement agency",
                audience: "Government",
              },
              {
                quote: "AUKUS compliance requirements meant we needed to map every Tier 2 and Tier 3 supplier back to country of origin. TradePath automated what was previously a 6-month manual exercise.",
                author: "Defence Supply Chain Manager",
                company: "Defence prime subcontractor",
                audience: "Defence",
              },
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white rounded-lg p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] relative"
                data-testid={`testimonial-card-${i}`}
              >
                <div className="text-[#0097A7] text-5xl font-serif leading-none mb-4">"</div>
                <p className="text-[#4E6974] text-sm leading-relaxed mb-6 italic">{testimonial.quote}</p>
                <div className="border-t border-[#E0E0E0] pt-4">
                  <p className="text-[#0D3B4F] font-semibold text-sm">{testimonial.author}</p>
                  <p className="text-[#4E6974] text-xs">{testimonial.company}</p>
                  <Badge className="bg-[#0097A7]/10 text-[#0097A7] text-[10px] mt-2">{testimonial.audience}</Badge>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="text-[#0097A7] font-semibold text-xl italic">"Where Innovation Meets Execution"</p>
            <a href="https://nuvantasolutions.com.au" className="text-[#4E6974] text-sm hover:underline mt-2 inline-block">nuvantasolutions.com.au</a>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection embedded />

      {/* CTA Section */}
      <section className="py-24 bg-[#0D3B4F]" id="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight mb-6">
              Ready to see your supply chain risk clearly?
            </h2>
            {ctaSubmitted ? (
              <p className="text-[#4DD0E1] text-lg font-medium" data-testid="cta-success">Thanks, we will be in touch shortly.</p>
            ) : (
              <>
                {ctaError && <p className="text-red-400 text-sm mb-4" data-testid="cta-error">{ctaError}</p>}
                {/* Honeypot - hidden from real users */}
                <input type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)}
                  className="absolute opacity-0 h-0 w-0 pointer-events-none" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-[#4DD0E1]"
                    data-testid="cta-email-input"
                    required
                  />
                  <Button 
                    className="w-full sm:w-auto bg-[#00707C] hover:bg-[#00707C]/90 text-white rounded-lg px-6 py-3 whitespace-nowrap"
                    data-testid="cta-submit-btn"
                    disabled={ctaLoading}
                    onClick={handleCtaSubmit}
                  >
                    {ctaLoading ? 'Sending...' : 'Get in touch'}
                  </Button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 bg-[#0A2533]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-white font-bold text-xl">Nuvanta Solutions</span>
                <span className="text-[#4DD0E1] text-xl">|</span>
                <span className="text-[#4DD0E1] font-semibold text-xl">TradePath</span>
              </div>
              <p className="text-[#4DD0E1] text-sm font-semibold mb-2">
                A <a href="https://nuvantasolutions.com.au" className="underline hover:text-white transition-colors">Nuvanta Solutions</a> product
              </p>
              <p className="text-white/60 text-sm">
                Nuvanta Solutions Pty Ltd | Melbourne | Brisbane
              </p>
              <a href="https://nuvantasolutions.com.au" className="text-[#4DD0E1] text-sm hover:underline">
                nuvantasolutions.com.au
              </a>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <a href="#about" className="text-white/60 hover:text-white text-sm transition-colors">About</a>
              <a href="#pricing" className="text-white/60 hover:text-white text-sm transition-colors">Pricing</a>
              <a href="/trust" className="text-white/60 hover:text-white text-sm transition-colors">Trust &amp; Security</a>
              <a href="#cta-section" className="text-white/60 hover:text-white text-sm transition-colors">Contact</a>
              <a href="mailto:info@nuvantasolutions.com.au" className="text-white/60 hover:text-white text-sm transition-colors">Email us</a>
              <a href="/terms" className="text-white/60 hover:text-white text-sm transition-colors">Terms of Service</a>
              <a href="/privacy" className="text-white/60 hover:text-white text-sm transition-colors">Privacy Policy</a>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-white/10 text-center text-white/40 text-sm">
            &copy; 2026 Nuvanta Solutions Pty Ltd. All rights reserved.
          </div>
        </div>
      </footer>
      </main>
    </div>
  );
};

export default LandingPage;
